/*
 * <prism-facets> — collection / search filtering and sorting.
 *
 * Re-renders the filter sidebar and the product grid in place via the Section
 * Rendering API, keeping the address bar and browser history in sync (so back /
 * forward work). Uses the Storefront filtering URL params produced by the form.
 *
 * Markup contract (rendered by the collection/search section):
 *   <prism-facets data-section="{{ section.id }}">
 *     <form data-facets-form>
 *       …filter inputs (name="filter.*"), optional <select name="sort_by" data-facets-sort>…
 *     </form>
 *     <div data-facets-filters> …active filters, remove links marked [data-facets-link]… </div>
 *     <div data-facets-results> …product grid + pagination links marked [data-facets-link]… </div>
 *   </prism-facets>
 */
if (!customElements.get('prism-facets')) {
  customElements.define(
    'prism-facets',
    class PrismFacets extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.getAttribute('data-section');

        this.onChange = this.onChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onPopState = this.onPopState.bind(this);

        var debounce = (window.PRISM && window.PRISM.debounce) || null;
        this.debouncedRender = debounce ? debounce(this.renderFromForm.bind(this), 350) : this.renderFromForm.bind(this);

        // Delegate on the element (not the form node) so listeners survive
        // the innerHTML swap that replaces the filter form on each render.
        this.addEventListener('input', this.onChange);
        this.addEventListener('change', this.onChange);
        this.addEventListener('submit', this.onSubmit);
        this.addEventListener('click', this.onClick);
        window.addEventListener('popstate', this.onPopState);
      }

      disconnectedCallback() {
        window.removeEventListener('popstate', this.onPopState);
        if (this.controller) this.controller.abort();
      }

      get form() {
        return this.querySelector('[data-facets-form]');
      }

      buildUrl() {
        var form = this.form;
        var path = (form && form.getAttribute('action')) || window.location.pathname;
        var params = new URLSearchParams();
        if (form) {
          new FormData(form).forEach(function (value, key) {
            if (value !== '') params.append(key, value);
          });
        }
        var qs = params.toString();
        return qs ? path + '?' + qs : path;
      }

      onSubmit(e) {
        if (!e.target.closest('[data-facets-form]')) return;
        e.preventDefault();
        this.renderFromForm();
      }

      onChange(e) {
        if (!e.target.closest('[data-facets-form]')) return;
        this.debouncedRender();
      }

      renderFromForm() {
        this.renderFromUrl(this.buildUrl(), true);
      }

      onClick(e) {
        var link = e.target.closest('[data-facets-link]');
        if (!link || !link.getAttribute('href')) return;
        e.preventDefault();
        this.renderFromUrl(link.getAttribute('href'), true);
      }

      onPopState() {
        this.renderFromUrl(window.location.href, false);
      }

      renderFromUrl(url, pushHistory) {
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();

        var fetchUrl = new URL(url, window.location.origin);
        fetchUrl.searchParams.set('section_id', this.sectionId);

        this.setAttribute('loading', '');
        fetch(fetchUrl.toString(), { signal: this.controller.signal })
          .then(function (r) {
            return r.text();
          })
          .then((html) => {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            this.swap(doc, '[data-facets-filters]');
            this.swap(doc, '[data-facets-results]');
            if (pushHistory) {
              var clean = new URL(url, window.location.origin);
              clean.searchParams.delete('section_id');
              window.history.pushState({}, '', clean.toString());
            }
            this.dispatchEvent(new CustomEvent('prism:facets:rendered', { bubbles: true }));
          })
          .catch(function (err) {
            if (err.name !== 'AbortError' && window.console) console.error('PRISM facets render failed', err);
          })
          .finally(() => {
            this.removeAttribute('loading');
          });
      }

      swap(doc, selector) {
        var fresh = doc.querySelector(selector);
        var current = this.querySelector(selector);
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      }
    }
  );
}
